import { useEffect, useState } from "react"
import { Hammer, Rocket } from "lucide-react"
import { cn } from "@/lib/utils"

export type StrategyMode = "builder" | "published"

export interface StrategyModeToggleProps {
  mode: StrategyMode
  onModeChange: (mode: StrategyMode) => void
  deployPulseId?: number | null
  className?: string
}

const MODES: Array<{ id: StrategyMode; label: string; icon: typeof Hammer }> = [
  { id: "builder", label: "Build", icon: Hammer },
  { id: "published", label: "Deploy", icon: Rocket },
]

const ACTIVE_W = 96
const INACTIVE_W_MOBILE = 40
const INACTIVE_W_DESKTOP = 96

function useIsSmBreakpoint() {
  const [isSm, setIsSm] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 640px)").matches : false,
  )
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 640px)")
    const handler = (e: MediaQueryListEvent) => setIsSm(e.matches)
    mq.addEventListener("change", handler)
    return () => mq.removeEventListener("change", handler)
  }, [])
  return isSm
}

export function StrategyModeToggle({
  mode,
  onModeChange,
  deployPulseId,
  className,
}: StrategyModeToggleProps) {
  const isSm = useIsSmBreakpoint()
  const activeIdx = MODES.findIndex((m) => m.id === mode)
  const inactiveW = isSm ? INACTIVE_W_DESKTOP : INACTIVE_W_MOBILE
  const trackWidth = ACTIVE_W + inactiveW
  const thumbLeft = activeIdx === 0 ? 0 : inactiveW
  const thumbRight = activeIdx === 0 ? inactiveW : 0

  return (
    <div
      role="tablist"
      aria-label="Strategy mode"
      className={cn(
        "relative h-6 inline-flex items-center rounded-full bg-black/[0.06] dark:bg-white/[0.05] ring-1 ring-inset ring-border",
        className,
      )}
      style={{ width: `${trackWidth}px` }}
    >
      <div
        aria-hidden="true"
        className={cn(
          "absolute top-0 bottom-0 rounded-full bg-background dark:bg-white/[0.12] ring-1 ring-inset ring-border/80 shadow-[0_1px_2px_rgba(0,0,0,0.08)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4)] transition-[left,right] duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)]",
        )}
        style={{ left: `${thumbLeft}px`, right: `${thumbRight}px` }}
      />
      {MODES.map((m) => {
        const isActive = mode === m.id
        return (
          <button
            key={m.id}
            role="tab"
            aria-selected={isActive}
            aria-label={m.label}
            onClick={() => onModeChange(m.id)}
            className={cn(
              "relative z-10 h-full inline-flex items-center justify-center rounded-full text-nav leading-none cursor-pointer transition-[width,color,transform] duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)] active:scale-[0.97]",
              isActive ? "w-24" : "w-10 sm:w-24",
              isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            <span className="relative inline-flex items-center justify-center shrink-0 w-[14px] h-[14px]">
              <m.icon
                size={14}
                strokeWidth={1.75}
                className={cn(
                  m.id === "published" && deployPulseId != null && "animate-deploy-rocket-out-in",
                )}
              />
              {m.id === "published" && deployPulseId != null && (
                <>
                  <span
                    key={`ring-${deployPulseId}`}
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 rounded-full border border-foreground/70 animate-deploy-ring-pulse"
                  />
                  <span
                    key={deployPulseId}
                    aria-hidden="true"
                    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                  >
                    <span className="block font-mono font-semibold tabular-nums text-[11px] leading-none text-foreground whitespace-nowrap animate-deploy-plus-one">
                      +1
                    </span>
                  </span>
                </>
              )}
            </span>
            <span
              className={cn(
                "overflow-hidden whitespace-nowrap transition-[max-width,opacity,margin-left] duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)]",
                isActive
                  ? "max-w-[60px] opacity-100 ml-1.5"
                  : "max-w-0 opacity-0 ml-0 sm:max-w-[60px] sm:opacity-100 sm:ml-1.5",
              )}
            >
              {m.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}
